import { ActivatedRoute } from '@angular/router';
import {
  BaseRouterStoreState,
  DEFAULT_ROUTER_FEATURENAME,
  getRouterSelectors,
  MinimalRouterStateSnapshot,
  RouterReducerState,
} from '@ngrx/router-store';
import {
  createFeatureSelector,
  createSelector,
  DefaultProjectorFn,
  MemoizedSelector,
} from '@ngrx/store';
import { Observable } from 'rxjs';

export interface EpcRouteData {
  showSpotlight?: boolean;
  showSearchBarOnMobile?: boolean;
}

type EpcRouteDataSelector = (state: {
  router: RouterReducerState<MinimalRouterStateSnapshot>;
}) => EpcRouteData | undefined;

/**
 * State Slice selector.
 */
export const selectRouter = createFeatureSelector<
  RouterReducerState<BaseRouterStoreState>
>(DEFAULT_ROUTER_FEATURENAME);

export type CurrentRouteType = {
  [key in keyof ActivatedRoute]: ActivatedRoute[key] extends Observable<infer U>
    ? U
    : ActivatedRoute[key];
};
export type CurrentRouteSelectorType = MemoizedSelector<
  object,
  CurrentRouteType,
  DefaultProjectorFn<CurrentRouteType>
>;
/**
 * ngrx generated selectors for route params.
 */
const getRouteSelectorsValue = getRouterSelectors(selectRouter);
export const selectCurrentRoute =
  getRouteSelectorsValue.selectCurrentRoute as CurrentRouteSelectorType;
export const {
  selectUrl,
  selectRouteParam,
  selectRouteParams,
  selectQueryParam: selectQueryParamNgrx,
  selectQueryParams: selectQueryParamsNgrx,
  selectRouteData: selectRouteDataNgrx,
} = getRouterSelectors(selectRouter);

/**
 * Same implementation for ngrx routeDataSelector which is strongly typed for epicuro
 */
export const selectRouteData: EpcRouteDataSelector = selectRouteDataNgrx;

/**
 * Abstracted query parameter selector, so if we change the router serializer we don't have to change
 * any other code than this. Also this makes it possible to override this in tests.
 */
export const selectQueryParams = createSelector(
  selectQueryParamsNgrx,
  (params) => params,
);

export const selectQueryParam = createSelector(
  selectQueryParamNgrx,
  (param) => param,
);

/**
 * Used to remove current query params
 */
export const selectNullishQueryParams = createSelector(
  selectQueryParamsNgrx,
  (param) =>
    Object.entries(param ?? {}).reduce(
      (res, [key]) => ({
        ...res,
        [key]: null,
      }),
      {},
    ),
);
